import { log } from '@domgen/shared/utils/log';
import { Calendar } from '../../../assets/icons/Calendar';
import { DollarSmall } from '../../../assets/icons/DollarSmall';
import { WashingMachineSmall } from '../../../assets/icons/WashingMachineSmall';
import { Wrench } from '../../../assets/icons/Wrench';
import { ConfirmationPanel } from '../ConfirmationPage.types';

export const formatPurchasedPlan = (
  brand: string,
  appliance?: string,
  startDate?: string,
  endDate?: string,
  planNumber?: string,
  netFirstPaymentAmount?: number,
  netFirstPaymentAmountAfterPromotionalPeriod?: number,
  discountTerm?: number,
  isReplacementCare?: boolean,
): ConfirmationPanel | undefined => {
  if (!brand || !appliance || !startDate || !planNumber || netFirstPaymentAmount === undefined) {
    return undefined;
  }

  log.debug({
    message: 'Formatting purchased plan confirmation panel with data',
    data: {
      brand,
      appliance,
      startDate,
      endDate,
      planNumber,
      netFirstPaymentAmount,
      netFirstPaymentAmountAfterPromotionalPeriod,
      discountTerm,
      isReplacementCare,
    },
  });

  const afterDiscountPayments = {
    label: `Monthly Payment (after ${discountTerm} months)`,
    icon: DollarSmall(),
    value: String(netFirstPaymentAmountAfterPromotionalPeriod),
  };

  const annualPlanPayments = isReplacementCare
    ? [
        {
          label: 'Payment',
          icon: DollarSmall(),
          value: String(netFirstPaymentAmount),
        },
      ]
    : [
        {
          label: `Monthly Payment ${discountTerm ? `(first ${discountTerm} months)` : ''}*`,
          icon: DollarSmall(),
          value: String(netFirstPaymentAmount),
        },
        ...(discountTerm && netFirstPaymentAmountAfterPromotionalPeriod
          ? [afterDiscountPayments]
          : []),
      ];

  const applianceCareTerms = isReplacementCare
    ? 'Charges will appear on your billing statement as “D&G Svc Plan.”'
    : "* Applicable taxes calculated monthly. Your discounted rate is shown above. Your plan documents (which we've emailed to you) contain the full breakdown of your payment plus any applicable taxes. Charges will appear on your billing statement as 'D&G Svc Plan'. Appliance Care Plan Terms & Conditions";

  const applianceCareTermsLinkText = `Hisense  ${isReplacementCare ? 'Replacement Care' : 'Care Plus'} Plan Terms & Conditions`;

  const data = [
    {
      label: 'Product',
      icon: WashingMachineSmall(),
      value: `${brand} ${appliance}`,
    },
    {
      label: 'Plan number',
      icon: Wrench(),
      value: planNumber,
    },
    {
      label: 'Plan start date',
      icon: Calendar(),
      value: startDate,
    },
    {
      label: 'Plan end date',
      icon: Calendar(),
      value: endDate,
    },
    ...annualPlanPayments,
  ];

  return {
    panelType: 'purchasePlan',
    panelTitle: 'Plan details',
    data,
    applianceCareTerms,
    applianceCareTermsLinkText,
  };
};

export const formatRegistrationData = (
  brand: string,
  appliance?: string,
  modelNumber?: string,
  serialNumber?: string,
  purchaseDate?: string,
  warrantyLength?: number,
): ConfirmationPanel | undefined => {
  if (
    ![brand, appliance, modelNumber, purchaseDate].every(
      (value) => typeof value === 'string' && value.trim() !== '',
    )
  ) {
    return undefined;
  }

  const panels = [
    {
      label: 'Product',
      icon: WashingMachineSmall(),
      value: `${brand} ${appliance}`,
    },
    {
      label: 'Manufacturer warranty length',
      icon: WashingMachineSmall(),
      value: `${warrantyLength} months`,
    },
    {
      label: 'Model number',
      icon: Wrench(),
      value: modelNumber,
    },
    serialNumber
      ? {
          label: 'Serial number',
          icon: Wrench(),
          value: serialNumber,
        }
      : undefined,
    {
      label: 'Purchase date',
      icon: Calendar(),
      value: purchaseDate,
    },
  ] as ConfirmationPanel['data'];

  return {
    panelType: 'registration',
    panelTitle: 'Registration details',
    data: panels.filter(Boolean),
  };
};
